import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { IoClose, IoMenu } from 'react-icons/io5';
import { PRODUCT_URLS } from '@podus/utils';

const LINKS = [{ label: 'How it works', href: '#how-it-works' }];

/**
 * Landing header.
 *
 * Deliberately not `LandingPagesNavbar` from `@podus/ui`: that navbar's Sign
 * In / Sign Up buttons point at the general Podus auth flow, which is the wrong
 * destination here — ambassadors sign in on this platform, and the programme is
 * invite-only so there is no public sign-up at all.
 */
export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    // Scrolling happens inside `.page-scroll-root`, not on the window, so both
    // the border-on-scroll and the scroll lock have to target that element.
    useEffect(() => {
        const root = document.querySelector('.page-scroll-root');
        if (!root) return;

        const onScroll = () => setScrolled(root.scrollTop > 8);
        onScroll();
        root.addEventListener('scroll', onScroll, { passive: true });
        return () => root.removeEventListener('scroll', onScroll);
    }, []);

    // Prevent the page scrolling behind the open mobile sheet.
    useEffect(() => {
        const root = document.querySelector<HTMLElement>('.page-scroll-root');
        if (!root) return;

        root.style.overflowY = open ? 'hidden' : '';
        return () => {
            root.style.overflowY = '';
        };
    }, [open]);

    return (
        <header
            className={`sticky top-0 z-[2000] bg-white/95 backdrop-blur transition-shadow ${
                scrolled ? 'border-b border-[#EEEEEE]' : ''
            }`}
        >
            {/* Gutters and cap match the landing sections below; the 68/76px
                heights are the `--nav-height` the hero's min-height subtracts. */}
            <div className="mx-auto flex h-17 max-w-[1920px] items-center justify-between px-4 lg:h-19 lg:px-16 xl:px-20">
                <Link to="/" className="flex items-center gap-2">
                    <img src="/logo.svg" alt="Podus" className="h-7 w-auto sm:h-8" />
                    <span className="text-sm font-bold text-secondary sm:text-[15px]">Ambassador</span>
                </Link>

                <nav className="hidden items-center gap-8 lg:flex">
                    {LINKS.map((link) => (
                        <a
                            key={link.href}
                            href={link.href}
                            className="text-[15px] text-[#4E4D4D] transition-colors hover:text-secondary"
                        >
                            {link.label}
                        </a>
                    ))}
                    <a
                        href={PRODUCT_URLS.podusai}
                        className="text-[15px] text-[#4E4D4D] transition-colors hover:text-secondary"
                    >
                        Podus AI
                    </a>
                </nav>

                {/* No admin entry point here — the programme's admin sign-in is
                    unadvertised, and staff navigate to it directly. */}
                <div className="hidden items-center gap-3 lg:flex">
                    <Link
                        to="/sign-in"
                        className="rounded-full bg-secondary px-6 py-2.5 text-[14px] font-semibold text-white transition-transform hover:scale-[1.02]"
                    >
                        Ambassador sign in
                    </Link>
                </div>

                <button
                    type="button"
                    title={open ? 'Close menu' : 'Open menu'}
                    aria-expanded={open}
                    onClick={() => setOpen((value) => !value)}
                    className="lg:hidden"
                >
                    {open ? <IoClose size={26} /> : <IoMenu size={26} />}
                </button>
            </div>

            {open && (
                <div className="animate-slide-in-right fixed inset-x-0 top-17 z-[2001] h-[calc(100dvh-68px)] bg-white px-6 py-6 lg:hidden">
                    <nav className="flex flex-col gap-1">
                        {LINKS.map((link) => (
                            <a
                                key={link.href}
                                href={link.href}
                                onClick={() => setOpen(false)}
                                className="border-b border-[#f2f2f2] py-4 text-[16px] text-secondary"
                            >
                                {link.label}
                            </a>
                        ))}
                        <a
                            href={PRODUCT_URLS.podusai}
                            className="border-b border-[#f2f2f2] py-4 text-[16px] text-secondary"
                        >
                            Podus AI
                        </a>
                    </nav>

                    <div className="mt-8 flex flex-col gap-3">
                        <Link
                            to="/sign-in"
                            onClick={() => setOpen(false)}
                            className="rounded-full bg-secondary py-3.5 text-center text-[15px] font-semibold text-white"
                        >
                            Ambassador sign in
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
}
